/**
 * Which squares on a card two men disagree about.
 *
 * `score_events` has recorded every write since the app shipped — the previous
 * value, the new one, who typed it, when it arrived and when it was decided —
 * and until now nothing read it. The one time it mattered, on the dry run, it
 * mattered a great deal: three men wrote the 3rd hole of Poulton's card within
 * a minute, the last one won on the clock, and a birdie that had already been
 * announced to twelve phones was withdrawn without a word to anybody. The
 * record was perfect and invisible.
 *
 * This turns that record into the question a signer should be asked before the
 * money goes final: these two numbers were typed by these two men at these two
 * times — which one is right?
 *
 * Pure, and deliberately kept away from the database and the screen: the rule
 * for "is this an argument" is the whole of the value here, and it wants to be
 * testable in isolation.
 */

export interface ScoreEvent {
  playerId: string
  holeNo: number
  state: 'holed' | 'pickedUp' | 'conceded'
  gross: number | null
  /** The session that wrote it — bound server-side, so it cannot be forged. */
  enteredBy: string
  /** When the server took it, ISO. Sorting on this orders the story. */
  enteredAt: string
}

export interface ContestedEntry {
  state: ScoreEvent['state']
  gross: number | null
  enteredBy: string
  enteredAt: string
  /** Whether this is the value currently on the card. */
  standing: boolean
}

export interface ContestedCell {
  playerId: string
  holeNo: number
  /** Each man's last word, oldest first. Always two or more. */
  entries: ContestedEntry[]
}

/** Two writes are the same score if they say the same thing about the hole. */
const same = (a: Pick<ScoreEvent, 'state' | 'gross'>, b: Pick<ScoreEvent, 'state' | 'gross'>) =>
  a.state === b.state && a.gross === b.gross

/**
 * The disagreements on a set of writes.
 *
 * Only each man's LAST word counts, which is what keeps the list honest in the
 * three ordinary cases that are not arguments at all:
 *
 *   - one man keeping the card for his foursome — every write is his, so there
 *     is only ever one last word, and golf has been scored that way forever;
 *   - a man correcting himself — same;
 *   - two men typing the same number, or one coming round to the other's after
 *     they talk about it on the next tee — the last words agree.
 *
 * A list that raises any of those gets scrolled past, and then the one that
 * matters gets scrolled past with it.
 */
export function contestedCells(events: ScoreEvent[]): ContestedCell[] {
  const byCell = new Map<string, ScoreEvent[]>()
  for (const e of events) {
    const key = `${e.playerId}|${e.holeNo}`
    const list = byCell.get(key)
    if (list) list.push(e)
    else byCell.set(key, [e])
  }

  const out: ContestedCell[] = []

  for (const list of byCell.values()) {
    const inOrder = [...list].sort((a, b) => a.enteredAt.localeCompare(b.enteredAt))

    // Each man's last word, keyed by the man.
    const lastWord = new Map<string, ScoreEvent>()
    for (const e of inOrder) lastWord.set(e.enteredBy, e)
    const words = [...lastWord.values()].sort((a, b) => a.enteredAt.localeCompare(b.enteredAt))

    if (words.length < 2) continue
    if (words.every((w) => same(w, words[0]!))) continue

    // The standing value is whatever the newest write on the cell says — not
    // the newest LAST WORD, because a man's older last word can still be the
    // one on the card if the newest write merely repeated it.
    const current = inOrder[inOrder.length - 1]!

    out.push({
      playerId: words[0]!.playerId,
      holeNo: words[0]!.holeNo,
      entries: words.map((w) => ({
        state: w.state,
        gross: w.gross,
        enteredBy: w.enteredBy,
        enteredAt: w.enteredAt,
        standing: same(w, current),
      })),
    })
  }

  /*
   * By hole, then by player. A signer walks the card hole by hole, and a list
   * that jumps about the scorecard is a list he cannot check off against it.
   */
  return out.sort((a, b) => a.holeNo - b.holeNo || a.playerId.localeCompare(b.playerId))
}
